import { Editor, Element, Range, Transforms } from 'slate'
import { identity } from 'ramda'
import { useEditor, useSelected, useSlate } from 'slate-react'
import React from 'react'

import { Absolute } from '../../../kiti/components/helpers/Position'
import { Col, Grid } from '../../../kiti/components/structure/Grid'
import { useColor } from '../../../kiti/components/theme/_hooks/colors'
import { useEditorState, useNodeMenuForm } from '../EditorContext'
import EditorIcon from '../EditorIcon'
import EditorMenuFormWrapper from '../EditorMenuFormWrapper'
import Icon from '../../../kiti/components/shapes/Icon'
import Input from '../../../kiti/components/form/Input'
import Panel from '../../../kiti/components/structure/Panel'
import Select from '../../../kiti/components/form/Select'
import Tag from '../../../kiti/components/presentation/Tag'
import Text from '../../../kiti/components/presentation/Text'

const getPrefix = code_start => {
  return (code_start || '').replace('#', '').replace('{{', ' ').replace('}}', ' ').split(' ').find(identity)
}

const PluginElement = ({ ...props }) => {
  if (props.element.presentation === 'inline') return <InlineElement {...props} />
  return <BlockElement {...props} />
}

const ElementActions = () => {
  const editor = useEditor()
  const { setMenu } = useEditorState()

  const handleClick = () => {
    const [_, selection] =
      Editor.above(editor, {
        match: n => !Editor.isEditor(n) && Element.isElement(n) && n.type === 'raw_interpolation'
      }) || []
    Transforms.select(editor, selection)
    setMenu('raw_interpolation')
  }

  return (
    <a style={{ flex: 0, display: 'inline-block' }} onClick={handleClick}>
      <Panel padding={0.2} bgColor="dark5" noBorder noRadius>
        <Icon name="RiPencilLine" color="white" />
      </Panel>
    </a>
  )
}

const BlockElement = ({ children, attributes, element, ...props }) => {
  const color = useColor('dark5')
  const selected = useSelected()

  const editor = useEditor()

  return (
    <div
      {...attributes}
      style={{
        padding: 0,
        boxShadow: selected && '0 0 0 2px #B4D5FF',
        border: `solid 1px ${color}`,
        display: element.presentation === 'inline' ? 'inline-block' : 'block',
        borderRadius: 5,
        position: 'relative'
      }}
    >
      <div contentEditable={false}>
        <Absolute top={0} right={0} zIndex={1}>
          {selected ? <ElementActions /> : <Tag color="light5" size="small" label={getPrefix(element.code_start)} />}
        </Absolute>
      </div>
      {children}
    </div>
  )
}

const InlineElement = ({ children, attributes, element, ...props }) => {
  const color = useColor('dark5')
  const selected = useSelected()

  return (
    <div
      {...attributes}
      style={{
        padding: 0,
        boxShadow: selected && '0 0 0 2px #B4D5FF',
        border: `solid 1px ${color}`,
        display: element.presentation === 'inline' ? 'inline-block' : 'block',
        borderRadius: 5
      }}
    >
      <Grid>
        {selected ? (
          <ElementActions />
        ) : (
          <Panel
            bgColor="dark5"
            paddingV={0}
            paddingH={0.4}
            margin={0}
            noRadius
            noBorder
            align="center"
            justify="center"
          >
            <div contentEditable={false}>
              <Text size="small" color="white">
                {getPrefix(element.code_start)}
              </Text>
            </div>
          </Panel>
        )}

        <div style={{ flex: 50 }}>{children}</div>
      </Grid>
    </div>
  )
}

const isActive = editor => {
  const [match] = Editor.nodes(editor, {
    match: n => !Editor.isEditor(n) && Element.isElement(n) && n.type === 'raw_interpolation'
  })
  return !!match
}

const toggle = (editor, menu, setMenu) => {
  const active = isActive(editor)
  const { selection } = editor

  if (menu === 'raw_interpolation') return
  if (!selection) return

  // if (active) {
  // if (Point.equals(selection?.anchor, selection?.focus)) {
  // const offset = Editor.leaf(editor, selection?.focus)[0]?.text?.length
  // const isInverted = Point.isBefore(selection?.anchor, selection?.focus)
  // const init = isInverted ? selection.focus : selection?.anchor
  // const end = isInverted ? selection.anchor : selection?.focus

  // Transforms.select(editor, { anchor: { ...init, offset: 0 }, focus: { ...end, offset } })
  // }
  // }

  setTimeout(() => setMenu('raw_interpolation'), 200)
}

const ToggleIcon = ({ ...props }) => {
  const { menu, setMenu } = useEditorState()
  const editor = useSlate()

  if (!editor.canUseRawInterpolation) return false

  const active = isActive(editor)

  return <EditorIcon onClick={() => toggle(editor, menu, setMenu)} icon="RiCodeBoxLine" active={active} {...props} />
}

const unwrapInterpolation = editor => {
  Transforms.unwrapNodes(editor, {
    match: n => !Editor.isEditor(n) && Element.isElement(n) && n.type === 'raw_interpolation'
  })
}

const wrapInterpolation = (editor, { presentation, code_start, code_end }) => {
  if (isActive(editor)) {
    unwrapInterpolation(editor)
  }
  const { selection } = editor
  const isCollapsed = selection && Range.isCollapsed(selection)
  const interpolationBlock = {
    type: 'raw_interpolation',
    presentation,
    code_start,
    code_end,
    children: isCollapsed ? [{ text: '' }] : []
  }

  if (isCollapsed) {
    Transforms.insertNodes(editor, interpolationBlock)
  } else {
    Transforms.wrapNodes(editor, interpolationBlock, { split: true })
    Transforms.collapse(editor, { edge: 'end' })
  }
}

const Menu = () => {
  const editor = useSlate()
  const active = isActive(editor)
  const { menu, setMenu } = useEditorState()
  let selection = null
  if (active) selection = Editor.node(editor, editor.selection)[0]
  const [form, setForm, handleChange] = useNodeMenuForm(
    editor,
    'raw_interpolation',
    selection?.presentation !== 'block'
  )

  if (menu !== 'raw_interpolation') return false

  const handleClose = () => {
    if (form.code_start && form.code_end) {
      wrapInterpolation(editor, form)
      // } else {
      // unwrapInterpolation(editor)
    }
  }

  const handleRemove =
    active &&
    (() => {
      unwrapInterpolation(editor)
    })

  const onKeyPress = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleClose()
      setMenu()
    }
  }

  return (
    <EditorMenuFormWrapper title="Configuração da interpolação" onClose={handleClose} onRemove={handleRemove}>
      <Grid gap={0.5}>
        <Col flex={1}>
          <Select
            placeholder="Apresentação"
            size="xsmall"
            options={['inline', 'block']}
            value={form.presentation}
            onChange={value => handleChange('presentation', value)}
          />
        </Col>

        <Col flex={1}>
          <Input
            onKeyPress={onKeyPress}
            placeholder="Abertura da interpolação"
            size="xsmall"
            value={form.code_start}
            onChange={value => handleChange('code_start', value)}
          />
        </Col>

        <Col flex={1}>
          <Input
            onKeyPress={onKeyPress}
            placeholder="Fechamento da interpolação"
            size="xsmall"
            value={form.code_end}
            onChange={value => handleChange('code_end', value)}
          />
        </Col>

        <Col flex={1}></Col>
      </Grid>
    </EditorMenuFormWrapper>
  )
}

const extend = editor => {
  const { insertData, insertText, isInline } = editor

  editor.isInline = element =>
    (['raw_interpolation'].includes(element.type) && element.presentation !== 'block') || isInline(element)

  return editor
}

const RawInterpolationPlugin = {
  extend,
  isActive,
  toggle,
  ToggleIcon,
  Menu,
  Element: PluginElement
}

export default RawInterpolationPlugin
